import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CreateClientPdf,
  GetPlanType,
  GetProducts,
  GetStateList,
  GetSuffixDropDown,
} from '../../api/proposals';
import { GetPdfFile } from '../../api/pdfdownload';
import { base64ToBlob, blobToDownloadableUrl, downloadPdfFile } from '../Utilities/HelperMethods';
import { format } from 'date-fns';
import { useUser } from 'context/UserContext';

const ClientCreationWrapper = (Comp, rest) => {
  return function WrappedComponent(props) {
    const { client, versionData, selectedVersionInfo, shouldDownload, isPdfAvailable } = props;
    const [pdfTempPath, setPdfTempPath] = useState({});
    const [pdfUrl, setPdfUrl] = useState('');
    const birthDate = new Date(versionData?.dateOfBirth);
    const formatedDateOfBirth = versionData?.dateOfBirth ? format(birthDate, 'MM-dd-yyyy') : null;
    const user = useUser();

    const ridermap = useMemo(
      () =>
        versionData?.Riders?.map((item) => ({
          BenOption: item.benOption,
          BenType: item.benType,
          Name: item.name,
        })),
      [versionData?.Riders]
    );

    const pdfVersionData = useMemo(() => ({
      IllustrationID: versionData?.id,
      ProducerID: versionData?.ProducerID,
      FirstName: versionData?.firstName,
      LastName: versionData?.lastName,
      Suffix: versionData?.suffix,
      EffectiveDate: versionData?.effectiveDate ?? new Date().toISOString(),
      DoB: versionData?.dateOfBirth,
      DateOfBirth: formatedDateOfBirth,
      Age: versionData?.age,
      Gender: versionData?.gender,
      State: versionData?.residentState,
      AnnuitantFirstName: versionData?.annuitantFirstName,
      AnnuitantLastName: versionData?.annuitantLastName,
      AnnuitantGender: versionData?.annuitantGender,
      AnnuitantAge: versionData?.annuitantAge,
      Product: versionData?.product,
      Fund: versionData?.planType,
      Riders: {
        Rider: ridermap,
      },
      Amount: versionData?.amount * 1,
      TaxStatus: versionData?.taxStatus,
      Rmd: 'no',
      withdrawals: versionData?.withDrawls || [],
    }), [
      versionData?.id,
      versionData?.ProducerID,
      versionData?.firstName,
      versionData?.lastName,
      versionData?.suffix,
      versionData?.effectiveDate,
      versionData?.dateOfBirth,
      formatedDateOfBirth,
      versionData?.age,
      versionData?.gender,
      versionData?.residentState,
      versionData?.annuitantFirstName,
      versionData?.annuitantLastName,
      versionData?.annuitantGender,
      versionData?.annuitantAge,
      versionData?.product,
      versionData?.planType,
      ridermap,
      versionData?.amount,
      versionData?.taxStatus,
      versionData?.withDrawls,
    ]);

    const createPDF = useMutation({
      mutationFn: async () => await CreateClientPdf(pdfVersionData)(),
    });

    // Handle createPDF success with useEffect
    useEffect(() => {
      if (createPDF.isSuccess && createPDF.data) {
        const data = createPDF.data;
        if (data?.fullPath && data?.encodedDoc !== 'ERROR' && data?.fullPath !== 'ERROR') {
          const pdfFilePathParts = data.fullPath.split('\\');
          setPdfTempPath({
            folder: pdfFilePathParts[pdfFilePathParts.length - 2],
            document: pdfFilePathParts[pdfFilePathParts.length - 1],
          });
        } else {
          setPdfUrl('ERROR');
        }
      }
    }, [createPDF.isSuccess, createPDF.data]);

    const handleCreatePDF = useCallback(() => {
      if (pdfVersionData !== undefined && pdfVersionData.hasOwnProperty('FirstName') === true) {
        createPDF.mutate();
      }
    }, [pdfVersionData]);

    const DownloadPdfMutation = useMutation({
      mutationFn: async () => await GetPdfFile(pdfTempPath)(),
    });

    // Handle DownloadPdfMutation success with useEffect
    useEffect(() => {
      if (DownloadPdfMutation.isSuccess && DownloadPdfMutation.data) {
        const processDownload = async () => {
          const blob = await base64ToBlob(DownloadPdfMutation.data, { type: 'application/pdf' });
          const url = blobToDownloadableUrl(blob);
          setPdfUrl(url);
          if (shouldDownload && shouldDownload === true) {
            downloadPdfFile(url, versionData?.firstName);
          }
        };
        processDownload();
      }
    }, [
      DownloadPdfMutation.isSuccess,
      DownloadPdfMutation.data,
      shouldDownload,
      versionData?.firstName,
    ]);

    useEffect(() => {
      if (Object.keys(pdfTempPath).length > 0) {
        DownloadPdfMutation.mutate();
      }
    }, [pdfTempPath]);

    const { isPending: createPdfLoading } = createPDF;

    const ProductsQuery = useQuery({
      queryKey: ['GetProducts'],
      queryFn: async () => await GetProducts()(),
      enabled: false,
    });

    const SuffixQuery = useQuery({
      queryKey: ['Suffix'],
      queryFn: async () => await GetSuffixDropDown()(),
      enabled: false,
    });

    const StatesQuery = useQuery({
      queryKey: ['GetStateList'],
      queryFn: async () => await GetStateList()(),
      enabled: false,
    });

    const planTypeQuery = useQuery({
      queryKey: ['ProductOptions', selectedVersionInfo?.productID],
      queryFn: async () =>
        await GetPlanType(selectedVersionInfo?.productID ? selectedVersionInfo?.productID : '')(),
      enabled: false,
    });

    useEffect(() => {
      if (selectedVersionInfo?.productID) {
        planTypeQuery.refetch();
      }
    }, [selectedVersionInfo?.productID]);

    useEffect(() => {
      ProductsQuery.refetch();
    }, []);

    useEffect(() => {
      SuffixQuery.refetch();
    }, []);

    useEffect(() => {
      StatesQuery.refetch();
    }, []);

    const {
      isPending: ProductsQueryLoading,
      isSuccess: ProductcQuerySuccess,
      data: ProductsData,
    } = ProductsQuery;
    const {
      isPending: SuffixQueryLoading,
      isSuccess: SuffixQuerySuccess,
      data: SuffixData,
    } = SuffixQuery;
    const {
      isPending: StatesQueryLoading,
      isSuccess: StatesQuerySuccess,
      data: StatesData,
    } = StatesQuery;
    const {
      isPending: planTypeQueryLoading,
      isSuccess: planTypeQuerySuccess,
      data: planTypeData,
    } = planTypeQuery;
    const { isPending: createPDFLoading, isError: createPDFError } = createPDF;
    const { isPending: DownloadPdfMutationLoading, isError: DownloadPdfMutationError } =
      DownloadPdfMutation;

    const productLabel = ProductsData?.find((p) => p.value === versionData?.product)?.text;
    const stateLabel = StatesData?.states?.find((st) => st.Value === client?.residentState)?.Key;
    const planTypeLabel = planTypeData?.find((pt) => pt.value === versionData?.planType)?.text;

    return (
      <Comp
        {...rest}
        {...props}
        isLoading={
          ProductsQueryLoading ||
          SuffixQueryLoading ||
          StatesQueryLoading ||
          (selectedVersionInfo?.productID ? planTypeQueryLoading : false)
        }
        productLabel={productLabel || ''}
        state={stateLabel || ''}
        planTypeLabel={planTypeLabel || ''}
        createPdfLoading={createPdfLoading}
        handleCreatePDF={handleCreatePDF}
        client={client}
        versionData={versionData}
        selectedVersionInfo={selectedVersionInfo}
        pdfUrl={pdfUrl}
        isLoadingPdf={DownloadPdfMutationLoading === true || createPDFLoading === true}
        isPdfAvailable={isPdfAvailable}
      />
    );
  };
};

export default ClientCreationWrapper;
